import classNames from 'classnames';
import EcoIconSwiperArrow from 'eco-design-micro/icons/eco-icon-swiper-arrow';
import { ThemeWBProps } from 'eco-design-micro/props/const';
import { ImgVideoProps } from 'eco-design-micro/props/img/ImgVideoProps';
import { TitleDescProps } from 'eco-design-micro/props/msg/TitleDescProps';
import gtm from 'eco-design-reliance/gtm';
import EcoImg from 'eco-design/base/eco-img';
import EcoLink from 'eco-design/base/eco-link';
import EcoSection from 'eco-design/base/eco-section';
import EcoVideo from 'eco-design/base/eco-video';
import React, { useEffect, useRef, useState } from 'react';
import { Autoplay, Navigation, Pagination, Scrollbar } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/scss';
import { AutoplayOptions, Swiper as SwiperType } from 'swiper/types';
import { useAnimationNavigationRight } from './hooks/useAnimationNavigationRight';
import { useSwiperAutoplayOnVisible } from './hooks/useSwiperAutoplayOnVisible';
import './index.scss';

export interface EcoSectionCarouselItemProps extends ImgVideoProps, TitleDescProps {
    icon: string;
    title: string;
    desc: string;
    loop?: boolean;
    showPlayIcon?: boolean;
    autoPlay?: boolean;
    isLink?: boolean;
    link?: string;
    target?: '_blank' | '_self' | '';
    dataLayer?: any;
}

export interface EcoSectionCarouselProps {
    /**
     * 布局方式-文案
     */
    headerLayout?: 'header-top' | 'header-bottom';
    /**
     * 主题色
     */
    theme?: ThemeWBProps;
    /**
     * 列表
     */
    items: EcoSectionCarouselItemProps[];
    /**
     * 标题
     */
    title?: string;
    /**
     * 描述
     */
    desc?: string;
    /**
     * label
     */
    label?: string;
    /**
     * 分页
     */
    pagination?: boolean;
    /**
     * label字体色
     */
    labelColor?: string;
    /**
     * 是否开启自动轮播
     */
    swiperAutoPlay?: boolean;
    /**
    /**
     * 自动轮播参数
     */
    autoplay?: AutoplayOptions & {
        animationNavigationRight?: boolean;
        viewport?:
            | boolean
            | {
                  open: boolean;
                  threshold?: number;
                  initialCheck?: boolean;
              };
    };
    /**
     * 自定义类名
     */
    className?: string;
    /**
     * Swiper 的 scrollbar 配置
     */
    scrollbar?: {
        hide?: boolean;
        autoHide?: boolean;
        draggable?: boolean;
        dragSize?: number;
        snapOnRelease?: boolean;
    };
    /**
     * 获取实例
     */
    getSwiperDom?: (data: SwiperType) => void;

    gtmTitle?: string;

    isOpenDotsGTM?: boolean;
}

const DEFAULT_AUTOPLAY: EcoSectionCarouselProps['autoplay'] = {
    delay: 3000,
    stopOnLastSlide: false,
    disableOnInteraction: false,
    animationNavigationRight: false,
    viewport: false,
};

/**
 * 轮播图/视频(标题 + 描述)
 */
const EcoSectionCarousel: React.FC<EcoSectionCarouselProps> = ({
    title = '',
    desc = '',
    label = '',
    labelColor = '#000',
    headerLayout = 'header-bottom',
    theme = 'white',
    items = [],
    pagination,
    swiperAutoPlay = false,
    className,
    gtmTitle,
    isOpenDotsGTM = false,
    autoplay = DEFAULT_AUTOPLAY,
    scrollbar = {
        hide: false,
        draggable: true,
        autoHide: true,
        dragSize: 0,
        snapOnRelease: false,
    },
    getSwiperDom = () => {},
}) => {
    const prefixCls = `eco-section-carousel`;
    const classes = classNames(prefixCls, {
        [`${className}`]: className,
        [`${prefixCls}-theme-${theme}`]: theme,
        [`${prefixCls}-length-${items.length}`]: items.length,
    });
    const curAutoplay = { ...DEFAULT_AUTOPLAY, ...autoplay };

    const [spaceBetweenN, setSpaceBetweenN] = useState(24);
    useEffect(() => {
        const initHandle = () => {
            const clientWidth = document.body.clientWidth;
            if (clientWidth >= 1024) {
                setSpaceBetweenN(24);
            } else {
                setSpaceBetweenN(16);
            }
        };
        initHandle();
    }, []);

    const dataE = {
        label,
        labelColor,
        title,
        desc,
    };

    const { progress, resetProgress } = useAnimationNavigationRight(curAutoplay, swiperAutoPlay);
    // 控制在视口才轮播
    const swiperRef = useRef<SwiperType>();
    useSwiperAutoplayOnVisible(
        swiperRef,
        typeof curAutoplay.viewport === 'boolean' ? { open: curAutoplay.viewport, resetProgress } : { ...curAutoplay.viewport, resetProgress },
        swiperAutoPlay,
    );

    const handleNavigationClick = (direction: 'prev' | 'next') => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'product_detail_page',
            list_name: gtmTitle || 'none',
            button_name: direction === 'prev' ? 'Left switch' : 'Right switch',
        });
    };

    // 给 swiper-pagination-bullet 添加点击事件
    useEffect(() => {
        if (!isOpenDotsGTM) return;
        const bullets = document.querySelectorAll('.swiper-pagination-bullet');

        bullets.forEach((bullet, index) => {
            bullet.addEventListener('click', () => {
                console.log(`点击了第 ${index + 1} 个指示点`);
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'tab_click',
                    button_name: `carousel dots_${index + 1}`,
                    list_name: items[index]?.title || 'none',
                });
            });
        });
    }, [isOpenDotsGTM]);

    return (
        <div className={classes}>
            <EcoSection {...dataE} />
            <div className={`${prefixCls}-container`}>
                <Swiper
                    slidesPerView={'auto'}
                    autoplay={swiperAutoPlay ? curAutoplay : undefined}
                    spaceBetween={spaceBetweenN}
                    navigation={{
                        nextEl: '.eco-swiper-button-next',
                        prevEl: '.eco-swiper-button-prev',
                    }}
                    pagination={{
                        el: '.eco-swiper-pagination',
                        clickable: true,
                    }}
                    scrollbar={scrollbar}
                    modules={[Navigation, Pagination, Autoplay, Scrollbar]}
                    onTransitionStart={resetProgress}
                    onSwiper={(swiper) => {
                        getSwiperDom(swiper);
                        swiperRef.current = swiper; // 直接获取原生实例
                    }}
                >
                    {items.map((carouselItem: EcoSectionCarouselItemProps, index: number) => {
                        return (
                            <SwiperSlide key={index} className={`${prefixCls}-item`}>
                                {carouselItem?.isLink ? (
                                    <EcoLink href={carouselItem?.link} target={carouselItem?.target} dataLayer={carouselItem?.dataLayer || {}}>
                                        <div className={`${prefixCls}-item-header ${prefixCls}-layout-${headerLayout}`}>
                                            {!!carouselItem.icon && (
                                                <div className={`${prefixCls}-item-icon`}>
                                                    <EcoImg pc={carouselItem.icon} />
                                                </div>
                                            )}
                                            {!!carouselItem.title && <p className={`${prefixCls}-item-title`}>{carouselItem.title}</p>}
                                            {!!carouselItem.desc && <p className={`${prefixCls}-item-desc`}>{carouselItem.desc}</p>}
                                        </div>
                                        <div className={`${prefixCls}-item-img`}>
                                            {!carouselItem?.isVideo ? (
                                                <EcoImg pc={carouselItem.pc} pad={carouselItem.pad} h5={carouselItem.h5} />
                                            ) : (
                                                <EcoVideo pc={carouselItem.pc} autoPlay={carouselItem?.autoPlay} loop={carouselItem?.loop} />
                                            )}
                                        </div>
                                    </EcoLink>
                                ) : (
                                    <>
                                        <div className={`${prefixCls}-item-header ${prefixCls}-layout-${headerLayout}`}>
                                            {!!carouselItem.icon && (
                                                <div className={`${prefixCls}-item-icon`}>
                                                    <EcoImg pc={carouselItem.icon} />
                                                </div>
                                            )}
                                            {!!carouselItem.title && <p className={`${prefixCls}-item-title`}>{carouselItem.title}</p>}
                                            {!!carouselItem.desc && <p className={`${prefixCls}-item-desc`}>{carouselItem.desc}</p>}
                                        </div>
                                        <div className={`${prefixCls}-item-img`}>
                                            {!carouselItem?.isVideo ? (
                                                <EcoImg pc={carouselItem.pc} pad={carouselItem.pad} h5={carouselItem.h5} />
                                            ) : (
                                                <EcoVideo pc={carouselItem.pc} autoPlay={carouselItem?.autoPlay} loop={carouselItem?.loop} />
                                            )}
                                        </div>
                                    </>
                                )}
                            </SwiperSlide>
                        );
                    })}
                    <div className={`eco-swiper-control ${curAutoplay.animationNavigationRight ? 'animation' : ''} eco-swiper-control-${theme}`}>
                        {/* style 控制显隐，避免出现功能问题 */}
                        <div className="eco-swiper-pagination" style={{ display: pagination ? 'flex' : 'none' }}></div>
                        <div className="eco-swiper-navigation">
                            <div className="eco-swiper-button-prev" onClick={() => handleNavigationClick('prev')}>
                                <EcoIconSwiperArrow className="eco-swiper-button-icon" />
                            </div>
                            <div className="eco-swiper-button-next" onClick={() => handleNavigationClick('next')}>
                                <EcoIconSwiperArrow className="eco-swiper-button-icon" />
                                {curAutoplay.animationNavigationRight && (
                                    <svg className="eco-swiper-button-icon-animation" width="120" height="120" viewBox="0 0 120 120">
                                        <circle
                                            cx="60"
                                            cy="60"
                                            r="50"
                                            fill="none"
                                            stroke="#3279E1"
                                            strokeWidth="8"
                                            strokeLinecap="round"
                                            strokeDasharray={2 * Math.PI * 50}
                                            strokeDashoffset={2 * Math.PI * 50 * (1 - progress / 100)}
                                            transform="rotate(-90 60 60)"
                                        />
                                    </svg>
                                )}
                            </div>
                        </div>
                    </div>
                </Swiper>
            </div>
        </div>
    );
};

export default EcoSectionCarousel;
